<template>
    <app-modal :open="open" :heading="heading" @close="close">

        <form :id="formId" @submit.prevent="save">

            <app-alert v-if="message" :message="message" />

            <!-- Read-only details (e.g. the ID of the record being edited) -->
            <div
                v-for="row in info"
                :key="row.label"
                class="mb-3 sm:col-span-4 block text-sm leading-6 text-left text-white"
            >
                <span class="font-black">{{ row.label }}:</span> {{ row.value }}
            </div>
            <hr v-if="info.length && separated" class="my-8 border-gray-950" />

            <template v-for="field in fields" :key="field.key">
                <app-form-row :label="field.label || ''" :id="`${formId}-${field.key}`">

                    <select
                        v-if="field.type === 'select'"
                        :id="`${formId}-${field.key}`"
                        v-model="form[field.key]"
                        :required="field.required"
                        class="w-full"
                    >
                        <option value="" selected disabled>{{ field.placeholder }}</option>
                        <optgroup v-for="(options, group) in field.groups" :key="group" :label="group">
                            <option v-for="option in options" :key="option.value" :value="option.value">{{ option.label }}</option>
                        </optgroup>
                        <option v-for="option in field.options" :key="option.value" :value="option.value">{{ option.label }}</option>
                    </select>

                    <input
                        v-else-if="field.type === 'checkbox'"
                        :id="`${formId}-${field.key}`"
                        type="checkbox"
                        v-model="form[field.key]"
                        :true-value="1"
                        :false-value="0"
                        class="block focus:ring-2 focus:ring-inset focus:ring-white"
                    />

                    <input
                        v-else-if="field.type === 'number'"
                        :id="`${formId}-${field.key}`"
                        type="number"
                        v-model="form[field.key]"
                        :required="field.required"
                        :min="field.min"
                        :max="field.max"
                        step="any"
                        :placeholder="field.placeholder"
                        @input="field.digitsOnly && keepDigits($event)"
                        class="w-full"
                    />

                    <!-- The popup is teleported to <body>: the modal's overflow would cut it -->
                    <AppDatePicker
                        v-else-if="field.type === 'date'"
                        :id="`${formId}-${field.key}`"
                        :model-value="form[field.key]"
                        @update:model-value="form[field.key] = $event || ''"
                        :clearable="!field.required"
                        teleport
                    />

                    <input
                        v-else
                        :id="`${formId}-${field.key}`"
                        :type="field.type || 'text'"
                        v-model="form[field.key]"
                        :required="field.required"
                        :placeholder="field.placeholder"
                        class="w-full"
                    />

                </app-form-row>
                <div v-if="field.hint" class="text-white leading-5 text-xs text-left italic mt-0 mb-4">{{ field.hint }}</div>
            </template>

        </form>

        <template #buttons>
            <button
                type="submit"
                :form="formId"
                class="primary w-full sm:w-auto"
                :disabled="isSaving"
            >
                {{ submitLabel }}
            </button>
        </template>

    </app-modal>
</template>

<script>
import { ref, watch, getCurrentInstance } from 'vue';
import { useToast } from 'vue-toastification';
import apiClient, { getErrorMessage } from '@/api/apiClient';
import { t, entityParams } from '@/i18n';
import AppModal from '@/components/common/AppModal.vue';
import AppFormRow from '@/components/common/AppFormRow.vue';
import AppAlert from '@/components/common/AppAlert.vue';
import AppDatePicker from '@/components/common/AppDatePicker.vue';

// Generic add/edit dialog. The parent describes the form (`fields`), where to save it
// (`saveUrl` + `buildPayload`) and this component handles the rest: form state,
// validation, saving state, error message, toasts and closing.
//
//   fields: [{ key, label, type: 'text'|'email'|'password'|'number'|'date'|'select'|'checkbox',
//              required, placeholder, hint, min, max, digitsOnly, options, groups }]
//   values: initial form values; assign a NEW object whenever the form must be (re)filled,
//           e.g. after loading the record to edit
//   info:   read-only { label, value } rows shown above the fields
//
// Emits: update:open(false) to close, opened when it opens, saved(responseData, payload) after a save.
export default {
    components: {
        AppModal,
        AppFormRow,
        AppAlert,
        AppDatePicker,
    },
    props: {
        open: {
            type: Boolean,
            required: true,
        },
        heading: {
            type: String,
            required: true,
        },
        // key of the thing in the translations ("category", "recurringBill"...), used to build the toast messages
        entity: {
            type: String,
            required: true,
        },
        // 'add' | 'edit'
        mode: {
            type: String,
            default: 'add',
        },
        submitLabel: {
            type: String,
            required: true,
        },
        fields: {
            type: Array,
            required: true,
        },
        values: {
            type: Object,
            default: () => ({}),
        },
        info: {
            type: Array,
            default: () => [],
        },
        separated: {
            type: Boolean,
            default: false,
        },
        saveUrl: {
            type: String,
            required: true,
        },
        buildPayload: {
            type: Function,
            required: true,
        },
    },
    emits: ['update:open', 'opened', 'saved'],
    setup(props, { emit }) {
        const toast = useToast();
        const formId = `form-modal-${getCurrentInstance().uid}`;
        const form = ref({});
        const message = ref('');
        const isSaving = ref(false);
        const isAdd = props.mode === 'add';

        const close = () => {
            emit('update:open', false);
        };

        const keepDigits = (event) => {
            event.target.value = event.target.value.replace(/[^0-9]/g, '');
        };

        // Refill the form whenever the parent hands over new values
        watch(() => props.values, (values) => {
            form.value = { ...values };
        }, { immediate: true });

        watch(() => props.open, (isOpen) => {
            message.value = '';
            if (isOpen) {
                emit('opened');
            } else {
                form.value = {};
            }
        });

        const save = () => {
            if (isSaving.value) return;

            isSaving.value = true;
            const payload = props.buildPayload(form.value);

            apiClient.post(props.saveUrl, payload)
                .then((response) => {
                    if (response.data.success === true) {
                        message.value = '';
                        emit('saved', response.data, payload);
                        close();
                        toast.success(t(isAdd ? 'crud.added' : 'crud.edited', entityParams(props.entity)));
                    } else {
                        console.error(`Failed to save ${props.entity}.`);
                        message.value = response.data.message;
                        toast.error(t(isAdd ? 'crud.failedCreate' : 'crud.failedEdit', entityParams(props.entity)));
                    }
                })
                .catch((error) => {
                    console.error('Error:', error);
                    message.value = getErrorMessage(error);
                    toast.error(t(isAdd ? 'crud.errorAdding' : 'crud.errorEditing', entityParams(props.entity)));
                })
                .finally(() => {
                    isSaving.value = false;
                });
        };

        return { formId, form, message, isSaving, close, keepDigits, save };
    },
};
</script>
